import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext, useLocation } from "react-router-dom";
import { usePatient } from "../../context/PatientContext";
import { useAuth } from "../../context/AuthProvider";
import { useTheme } from "../../context/ThemeContext";
import PatientInfoForm from "../Forms/PatientInfoForm";
import { motion } from "framer-motion";
import { Loader2, UserCircle2 } from "lucide-react";

const EditPatientInfo = () => {
  const { patientId, caseId, visitId } = useParams();
  const {
    fetchPatientData,
    patientData,
    setPatientData,
    doctorInfo
  } = usePatient();
  const { theme, isDarkMode } = useTheme();
  const { markSectionComplete } = useOutletContext();
  const { verifyTokenAndRedirect } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    verifyTokenAndRedirect();

    const loadPatientData = async () => {
      try {
        const data = await fetchPatientData(patientId, caseId, visitId);

        if (!data) {
          setError("Failed to load patient data");
          setLoading(false);
          return;
        }

        const updatedPatientData = {
          ...patientData,
          ...data
        };

        setPatientData(updatedPatientData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching patient data:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    loadPatientData();
  }, [patientId, caseId, visitId, fetchPatientData]);

  const onSubmitHandler = (updatedPatientInfo, options = {}) => {
    const isAutoSave = options?.isAutoSave;
    const updatedData = {
      ...patientData,
      patientInfo: patientData.patientInfo.map(info => ({
        ...info,
        ...updatedPatientInfo,
        DOCTOR_ID: doctorInfo.MANDALI_ID,
        CLINIC_ID: doctorInfo.CLINIC_ID
      }))
    };

    setPatientData(updatedData);
    markSectionComplete("info");

    if (!isAutoSave) {
      if (location.pathname.startsWith("/addpatient")) {
        navigate("/addpatient/symptoms");
      } else if (location.pathname.startsWith("/visits")) {
        navigate(`/visits/${patientId}/${caseId}/${visitId}/symptoms`);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="animate-spin mb-4" size={40} style={{ color: theme.primary }} />
        <p className="font-medium opacity-60" style={{ color: theme.text }}>Loading patient profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <UserCircle2 size={32} className="text-red-500" />
        </div>
        <h3 className="text-xl font-bold mb-2">Error Loading Data</h3>
        <p className="opacity-60 mb-6 max-w-xs">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 rounded-full font-bold text-white transition-all hover:scale-105"
          style={{ backgroundColor: theme.primary }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-10">
        <h3
          className="text-2xl md:text-4xl mb-3 tracking-tight"
          style={{
            fontFamily: theme.fonts?.serif,
            color: theme.text
          }}
        >
          Basic Information
        </h3>
        <div className="h-1 w-12 rounded-full mb-6" style={{ backgroundColor: theme.primary }} />
        <p className="text-sm md:text-base opacity-60 font-medium" style={{ color: theme.textSecondary }}>
          Updating the core identity and contact details of the patient.
        </p>
      </div>

      {patientData && (
        <PatientInfoForm
          initialValues={patientData}
          onSubmit={onSubmitHandler}
          onAutoSave={(values) => onSubmitHandler(values, { isAutoSave: true })}
          enableReinitialize={true}
          isFirstStep={true}
        />
      )}
    </div>
  );
};

export default EditPatientInfo;
